import { useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import {
    COMPANY_SELECTOR_BUTTON,
    COMPANY_SELECTOR_BUTTON_MOBILE
} from "components/TutorialMode/constants";
import AuthContext from "context/auth/authContext";
import ParamsContext from "context/params/paramsContext";
import { FILTER_NAMES } from "shared/types/enums";

import {
    getCompanyFilterButtonTitle,
    getRootCompanyButtonTitle
} from "../functions";
import CompanyFilterState from "./CompanyFilterState";
import Avatar from "./components/Avatar";
import CompanyFilterLoader from "./components/CompanyFilterLoader";
import FilterDesktop from "./FilterDesktop";
import FilterMobile from "./FilterMobile";
import useCompanyFilterContent from "./hooks/useCompanyFilterContent";

const CompanyFilter = () => {
    const { filterDropdownOptions, rootCompany } = useContext(ParamsContext);
    const { isSupportUser } = useContext(AuthContext);

    const { t } = useTranslation();

    const {
        rootCompanyType,
        isLoading,
        isDialogOpen,
        anchorEl,
        rootCompanyId,
        companyFilteredValues,
        toSm,
        toMd,
        setRootCompanyType,
        openDialog,
        closeDialog,
        openMenu,
        closeMenu
    } = useCompanyFilterContent();

    const buttonTitle = isSupportUser
        ? getRootCompanyButtonTitle(
              companyFilteredValues,
              rootCompanyId as string,
              rootCompany,
              t
          )
        : getCompanyFilterButtonTitle(
              companyFilteredValues,
              t("Button##all companies"),
              t("General##companies")
          );

    const renderButtons = () => {
        const handleClickFromMd = toMd ? openDialog : openMenu;

        return toSm ? (
            <Button
                id={COMPANY_SELECTOR_BUTTON_MOBILE}
                color={BUTTON_COLORS.White}
                size={BUTTON_SIZES.Small}
                variant={BUTTON_VARIANTS.IconOnly}
                icon={<Avatar filteredValues={companyFilteredValues} />}
                onClick={openDialog}
            />
        ) : (
            <Button
                id={COMPANY_SELECTOR_BUTTON}
                color={BUTTON_COLORS.White}
                size={BUTTON_SIZES.Small}
                variant={BUTTON_VARIANTS.IconLeft}
                icon={<Avatar filteredValues={companyFilteredValues} />}
                onClick={handleClickFromMd}
            >
                <span
                    css={css({
                        maxWidth: "142px",
                        textOverflow: "ellipsis",
                        overflowX: "hidden"
                    })}
                >
                    {buttonTitle}
                </span>
            </Button>
        );
    };

    const renderContent = () => (
        <CompanyFilterState
            isLoading={isLoading}
            isDialogOpen={isDialogOpen}
            anchorEl={anchorEl}
            rootCompanyType={rootCompanyType}
            closeDialog={closeDialog}
            closeMenu={closeMenu}
            setRootCompanyType={setRootCompanyType}
        >
            {toMd ? <FilterMobile /> : <FilterDesktop />}
        </CompanyFilterState>
    );

    const rootCompanyOptions = filterDropdownOptions[FILTER_NAMES.RootCompany];

    const isShowLoader =
        isLoading &&
        !isDialogOpen &&
        !anchorEl &&
        (!isSupportUser || rootCompanyOptions === undefined);

    return isShowLoader ? (
        <CompanyFilterLoader />
    ) : (
        <>
            {renderButtons()}
            {renderContent()}
        </>
    );
};

export default CompanyFilter;
